import { Button, Popover, Text } from '@shared/components';
import { InlineTextField } from '@shared/components/inline-text-field/InlineTextField';
import { Option, Select } from '@shared/components/react-hook-form';
import useLoadInitialValues from '@shared/components/react-hook-form/useLoadInitialValues';
import {
  LANE_HISTORY_LOOKBACK_PERIOD_OPTIONS,
  LaneHistoryDefaults,
  sanitizeLaneHistoryDefaults,
  setLaneHistoryDefaults,
} from '@shared/hooks/laneHistoryDefaults';
import { LaneHistoryFilters } from '@shared/hooks/useLaneHistoryFilters';
import { merge } from 'lodash';
import { FormProvider, useForm } from 'react-hook-form';

type DefaultsEntryProps = {
  filters: LaneHistoryFilters;
  laneHistoryDefaults: LaneHistoryDefaults;
  organizationId: string;
  resetLaneHistoryDefaults: LaneHistoryDefaults;
  setFilters: (filters: LaneHistoryFilters) => void;
  setSinceDaysAgo: (sinceDaysAgo: number) => void;
};

type DefaultsFormData = {
  originRadius: string;
  destinationRadius: string;
  sinceDaysAgo: string;
};

export function DefaultsEntry(p: DefaultsEntryProps) {
  const originRadius =
    p.filters.laneFilter?.origin?.radiusMiles.toString() ??
    p.laneHistoryDefaults.originRadiusMiles.toString();
  const destinationRadius =
    p.filters.laneFilter?.destination?.radiusMiles.toString() ??
    p.laneHistoryDefaults.destinationRadiusMiles.toString();
  return (
    <div>
      <DefaultsForm
        initialValues={{
          originRadius,
          destinationRadius,
          sinceDaysAgo: p.laneHistoryDefaults.sinceDaysAgo.toString(),
        }}
        organizationId={p.organizationId}
        resetLaneHistoryDefaults={p.resetLaneHistoryDefaults}
        setFilters={p.setFilters}
        setSinceDaysAgo={p.setSinceDaysAgo}
        filters={p.filters}
      />
    </div>
  );
}

type DefaultsFormProps = {
  initialValues: DefaultsFormData;
  organizationId: string;
  resetLaneHistoryDefaults: LaneHistoryDefaults;
  setFilters: (filters: LaneHistoryFilters) => void;
  setSinceDaysAgo: (sinceDaysAgo: number) => void;
  filters: LaneHistoryFilters;
};
function DefaultsForm(props: DefaultsFormProps) {
  const methods = useForm<DefaultsFormData>({
    defaultValues: props.initialValues,
  });
  useLoadInitialValues(methods.reset, props.initialValues);

  const getFormDefaults = (
    overrides: Partial<LaneHistoryDefaults> = {}
  ): LaneHistoryDefaults => {
    return sanitizeLaneHistoryDefaults({
      ...props.resetLaneHistoryDefaults,
      originRadiusMiles: parseFloat(methods.getValues('originRadius')),
      destinationRadiusMiles: parseFloat(
        methods.getValues('destinationRadius')
      ),
      sinceDaysAgo: parseFloat(methods.getValues('sinceDaysAgo')),
      ...overrides,
    });
  };

  const saveDefaults = (overrides: Partial<LaneHistoryDefaults> = {}) => {
    const defaults = getFormDefaults(overrides);
    setLaneHistoryDefaults(props.organizationId, defaults);
    const newFilters = merge({}, props.filters, {
      laneFilter: {
        origin: { radiusMiles: defaults.originRadiusMiles },
        destination: { radiusMiles: defaults.destinationRadiusMiles },
      },
    });
    props.setFilters(newFilters);
    props.setSinceDaysAgo(defaults.sinceDaysAgo);
  };

  const onSaveOriginRadius = () => {
    saveDefaults();
  };

  const onSaveDestinationRadius = () => {
    saveDefaults();
  };

  const onSaveSinceDaysAgo = (value: string) => {
    saveDefaults({ sinceDaysAgo: parseFloat(value) });
  };

  const onOpenChange = (open: boolean) => {
    if (!open) {
      saveDefaults();
    }
  };

  const className = 'flex flex-row items-baseline gap-2 text-gray-500';
  const fieldClassName = 'h-[28px] items-center justify-center';
  return (
    <Popover
      onOpenChange={onOpenChange}
      content={
        <div className="flex flex-col rounded-lg p-[12px] pt-[20px]">
          <div className="pb-4">
            <Text type="body-sm">Lane history defaults</Text>
          </div>
          <FormProvider {...methods}>
            <div className={className}>
              <InlineTextField
                fieldWrapperClassName={fieldClassName}
                name="originRadius"
                save={onSaveOriginRadius}
                required={false}
                type="number"
              />
              <div className="flex w-1/2 justify-start">
                <Text type="body-xs">origin radius (miles)</Text>
              </div>
            </div>
            <div className={className}>
              <InlineTextField
                fieldWrapperClassName={fieldClassName}
                name="destinationRadius"
                save={onSaveDestinationRadius}
                required={false}
                type="number"
              />
              <div className="flex w-1/2 justify-start">
                <Text type="body-xs">destination radius (miles)</Text>
              </div>
            </div>
            <div className={className}>
              <Select
                name="sinceDaysAgo"
                control={methods.control}
                size="small"
                aria-label="Default lookback period"
                onChange={onSaveSinceDaysAgo}
                renderValue={(value: string) => `Last ${value} days`}
              >
                {LANE_HISTORY_LOOKBACK_PERIOD_OPTIONS.map((days) => (
                  <Option key={days} value={days.toString()}>
                    Last {days} days
                  </Option>
                ))}
              </Select>
              <div className="flex w-1/2 justify-start">
                <Text className="text-left" type="body-xs">
                  lookback
                </Text>
              </div>
            </div>
          </FormProvider>
        </div>
      }
    >
      <Button variant="tertiary" size="small">
        Change lane history defaults
      </Button>
    </Popover>
  );
}
